import React, { useCallback, useEffect, useRef, useState } from "react";
import { MagnifyingGlass, X, Plus } from "@phosphor-icons/react";
import api from "@/lib/api";

/**
 * Iter-61.4.2 — Scalable Campaign multi-select for the Destination form.
 *
 * Replaces the preloaded `<select multiple>` that was backed by
 * `api.get("/campaigns")` (500-row cap). The prod campaign directory
 * can grow beyond 500; a campaign at position 537 was previously
 * invisible in New Destination and silently dropped on Edit save.
 *
 * Behaviour:
 *   • Selected chips render at the top (with resolved names).
 *   • "+ Add campaign" opens a searchable dropdown scoped to the
 *     currently-selected Buyer (`clientId`). Empty term → first 25
 *     alphabetical results; typing → debounced 300ms server search.
 *   • Unknown selections (ids not in the current search page) are
 *     hydrated once on mount via `POST /campaigns/resolve` so an
 *     Edit-load never silently loses a campaign.
 *   • Unresolvable ids (deleted / RBAC-restricted) are still
 *     preserved in `value` — the chip renders with a short id — so
 *     a passthrough save doesn't mutate the field.
 *
 * Empty `value` means "all campaigns for this destination's client
 * scope" — semantic preserved from Iter-61 for backwards-compat.
 */
export default function CampaignPicker({
  value = [],           // array of campaign ids
  onChange,             // (ids) => void
  clientId = "",        // scope for the picklist
  disabled = false,
  testIdPrefix = "campaign-picker",
}) {
  const [nameById, setNameById] = useState({}); // id -> name
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);

  // On mount / when `value` changes → resolve any unknown ids.
  useEffect(() => {
    let cancelled = false;
    const unknown = (value || []).filter((id) => id && !nameById[id]);
    if (!unknown.length) return () => {};
    (async () => {
      try {
        const { data } = await api.post("/campaigns/resolve", { ids: unknown });
        if (cancelled) return;
        const patch = {};
        (data.campaigns || []).forEach((c) => { patch[c.id] = c.name; });
        setNameById((prev) => ({ ...prev, ...patch }));
      } catch {
        // Best-effort — chip falls back to id prefix.
      }
    })();
    return () => { cancelled = true; };
  }, [value, nameById]);

  const runSearch = useCallback(async (query) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page_size: "25", sort: "name_asc",
      });
      if (clientId) params.set("client_id", clientId);
      const trimmed = (query || "").trim();
      if (trimmed) params.set("q", trimmed);
      const { data } = await api.get(`/campaigns?${params.toString()}`);
      setResults(data.campaigns || []);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (!open) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(term), 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [term, open, runSearch]);

  useEffect(() => {
    if (open && results.length === 0 && !term) runSearch("");
  }, [open, results.length, term, runSearch]);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const addId = (id, name) => {
    if (!id) return;
    if ((value || []).includes(id)) { setTerm(""); setOpen(false); return; }
    setNameById((prev) => name ? { ...prev, [id]: name } : prev);
    onChange?.([...(value || []), id]);
    setTerm("");
    setOpen(false);
  };

  const removeId = (id) => {
    onChange?.((value || []).filter((v) => v !== id));
  };

  return (
    <div ref={wrapRef} className="w-full" data-testid={`${testIdPrefix}-root`}>
      <div className="border border-black p-2 min-h-[42px] flex flex-wrap items-center gap-1"
           data-testid={`${testIdPrefix}-chips`}>
        {(value || []).length === 0 && (
          <span className="text-xs text-zinc-500 italic px-1">
            All campaigns for the selected client (leave empty to deliver every campaign)
          </span>
        )}
        {(value || []).map((id) => {
          const name = nameById[id] || id.slice(0, 8);
          return (
            <span key={id} data-testid={`${testIdPrefix}-chip-${id}`}
              className="inline-flex items-center gap-1 border border-black bg-zinc-100 px-2 py-0.5 text-[11px] font-mono">
              <span className="truncate max-w-[220px]" title={name}>{name}</span>
              {!disabled && (
                <button type="button" onClick={() => removeId(id)}
                  data-testid={`${testIdPrefix}-chip-remove-${id}`}
                  className="text-zinc-500 hover:text-red-700">
                  <X size={10} />
                </button>
              )}
            </span>
          );
        })}
        {!disabled && (
          <button type="button" onClick={() => setOpen((v) => !v)}
            data-testid={`${testIdPrefix}-add`}
            className="border border-black px-2 py-0.5 text-[11px] uppercase tracking-widest hover:bg-black hover:text-white inline-flex items-center gap-1">
            <Plus size={10} /> Add campaign
          </button>
        )}
      </div>

      {open && (
        <div className="relative">
          <div
            className="absolute left-0 right-0 top-1 border-2 border-black bg-white z-40 max-h-64 overflow-y-auto brut-shadow-sm"
            data-testid={`${testIdPrefix}-dropdown`}
          >
            <div className="p-2 border-b border-zinc-300 sticky top-0 bg-white">
              <div className="relative">
                <MagnifyingGlass size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input
                  autoFocus
                  type="text"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  placeholder={clientId ? "Search campaigns…" : "Search all campaigns…"}
                  className="w-full border border-black pl-6 pr-2 py-1 text-sm"
                  data-testid={`${testIdPrefix}-input`}
                />
              </div>
              {!clientId && (
                <div className="text-[10px] uppercase tracking-widest text-amber-700 mt-1">
                  No buyer selected — showing every campaign
                </div>
              )}
            </div>
            <div>
              {loading && (
                <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500">Loading…</div>
              )}
              {!loading && results.length === 0 && (
                <div className="px-3 py-2 text-xs uppercase tracking-widest text-zinc-500"
                     data-testid={`${testIdPrefix}-empty`}>
                  No campaigns match "{term}"
                </div>
              )}
              {results.map((c) => {
                const already = (value || []).includes(c.id);
                return (
                  <button
                    type="button" key={c.id}
                    onClick={() => addId(c.id, c.name)}
                    disabled={already}
                    data-testid={`${testIdPrefix}-option-${c.id}`}
                    className={`w-full text-left px-3 py-2 text-sm border-b border-zinc-100 ${already ? "opacity-40 cursor-not-allowed" : "hover:bg-zinc-100"}`}
                    title={c.name}
                  >
                    <span className="truncate block">{c.name}</span>
                    {c.client_name && (
                      <span className="text-[10px] uppercase tracking-widest text-zinc-500">
                        {c.client_name}{c.is_active === false ? " · paused" : ""}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
